import React, { useCallback, useState } from "react";
import ChartMoreMenu from "../ChartMoreMenu";
import ChartTypeMenu from "../ChartTypeMenu";
import IndicatorsMenu from "../IndicatorsMenu";
import DrawingsMenu from "../DrawingsMenu";
import useChartType from "../../hooks/useChartType";
import "./ChartToolbar.sass";

const MENU_NONE = null;
const MENU_CHART_TYPE = "chartType";
const MENU_INDICATORS = "indicators";
const MENU_DRAWINGS = "drawings";
const MENU_MORE = "more";

/**
 * Toolbar overlay rendered on top of the chart canvases.
 * Composes ChartTypeMenu (TV-06), IndicatorsMenu (TV-14), DrawingsMenu (TV-18) and the More menu (TV-04).
 * The pair is switched only in the Quotes header: there is always one chart (D20).
 *
 * @param {object}   props
 * @param {object}   props.chartSettingsApi    - { settings, updateSettings } from useChartSettings
 * @param {Function} props.onOpenCandleColors - () => void — opens CandleColorsModal
 * @param {object}   props.indicatorsApi       - useIndicators: indicators, add, remove, removeAll
 * @param {object}   props.favoriteIndicatorsApi - useFavoriteIndicators: favoriteIds, toggleFavorite
 * @param {(key: string) => void} props.onEditIndicator - ✏️ in the Current tab opens the indicator settings
 * @param {object}   props.drawingsApi         - useDrawings: drawings, add, remove, removeAll
 * @param {(id: string) => void} props.onEditDrawing - ✏️ in the Current tab opens the drawing settings
 */
const ChartToolbar = ({
  chartSettingsApi,
  onOpenCandleColors,
  indicatorsApi,
  favoriteIndicatorsApi,
  onEditIndicator,
  drawingsApi,
  onEditDrawing,
}) => {
  const [openMenu, setOpenMenu] = useState(MENU_NONE);

  const toggle = useCallback((menuId) => {
    setOpenMenu((current) => (current === menuId ? MENU_NONE : menuId));
  }, []);

  const close = useCallback(() => setOpenMenu(MENU_NONE), []);

  const { chartType, resolution, setChartType, setResolution } = useChartType(chartSettingsApi);

  return (
    <div className="chart-toolbar">
      <ChartTypeMenu
        chartType={chartType}
        resolution={resolution}
        onChartType={setChartType}
        onResolution={setResolution}
        chartSettingsApi={chartSettingsApi}
        isOpen={openMenu === MENU_CHART_TYPE}
        onToggle={() => toggle(MENU_CHART_TYPE)}
        onClose={close}
        onOpenCandleColors={onOpenCandleColors}
      />
      <IndicatorsMenu
        indicators={indicatorsApi.indicators}
        onAdd={indicatorsApi.add}
        onEdit={onEditIndicator}
        onRemove={indicatorsApi.remove}
        onRemoveAll={indicatorsApi.removeAll}
        favoriteIds={favoriteIndicatorsApi.favoriteIds}
        onToggleFavorite={favoriteIndicatorsApi.toggleFavorite}
        isOpen={openMenu === MENU_INDICATORS}
        onToggle={() => toggle(MENU_INDICATORS)}
        onClose={close}
      />
      <DrawingsMenu
        drawings={drawingsApi.drawings}
        onAddTool={drawingsApi.add}
        onEdit={onEditDrawing}
        onRemove={drawingsApi.remove}
        onRemoveAll={drawingsApi.removeAll}
        isOpen={openMenu === MENU_DRAWINGS}
        onToggle={() => toggle(MENU_DRAWINGS)}
        onClose={close}
      />
      <ChartMoreMenu
        chartSettingsApi={chartSettingsApi}
        isOpen={openMenu === MENU_MORE}
        onToggle={() => toggle(MENU_MORE)}
        onClose={close}
      />
    </div>
  );
};

export default ChartToolbar;
